import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faViber } from '@fortawesome/free-brands-svg-icons';
import { faSearch, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import AddEditReservationModal from './AddEditReservationModal';
import { useSalonCalendarState } from './SalonCalendarState';
import DatePickerButton from './DatePickerButton';
import useScrollLock from '../../hooks/useScrollLock';
import { buildReservationNotesDisplay } from '../../utils/reservationNotes';
import './Salon.css';

const ManageReservations = ({ salonId, userRole, currentPage: urlPage = 1, onPageChange }) => {
  const { t, getMonthName, formatWeddingDate, getDayName } = useLanguage();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const {
    currentDate,
    viewMode,
    applyDefaultViewFromSettings,
    getWeekDates,
    getMonthDates,
    navigateWeek,
    navigateMonth,
    navigateDay,
    goToToday,
    goToDate,
    handleSwitchToDayView,
    handleSwitchToWeekView,
    handleSwitchToMonthView,
    getFetchDateRange,
  } = useSalonCalendarState('week');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const searchTimeoutRef = useRef(null);

  const fetchCalendarSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const isMobile = window.innerWidth <= 768;
      
      const settingKey = isMobile ? 'calendar_view_mobile' : 'calendar_view_desktop';
      
      try {
        const response = await axios.get(`/api/salons/${salonId}/settings/${settingKey}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.value) {
          applyDefaultViewFromSettings(response.data.value);
        }
      } catch (error) {
        console.log('Calendar view setting not found, using default');
      }
    } catch (error) {
      console.error('Error fetching calendar settings:', error);
    }
  }, [salonId, applyDefaultViewFromSettings]);

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const { startDate, endDate } = getFetchDateRange();
      
      // Format dates as YYYY-MM-DD
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const start = formatDate(startDate);
      const end = formatDate(endDate);
      
      const response = await axios.get(`/api/salons/${salonId}/reservations?start_date=${start}&end_date=${end}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setReservations(response.data.reservations || []);
      setError('');
    } catch (error) {
      console.error('Error fetching reservations:', error);
      setError('Failed to fetch reservations');
    } finally {
      setLoading(false);
    }
  }, [salonId, getFetchDateRange]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    fetchCalendarSettings();
  }, [fetchCalendarSettings]);

  // Listen for window resize to update calendar view based on screen size
  useEffect(() => {
    const handleResize = () => {
      fetchCalendarSettings();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fetchCalendarSettings]);

  // Check for pre-filled data from appointment navigation
  useEffect(() => {
    console.log('ManageReservations checking for prefill data:', window.reservationPrefillData);
    if (window.reservationPrefillData) {
      console.log('Opening add modal with prefill data');
      setIsAddModalOpen(true);
    }
  }, []);

  // Build card instances for a given calendar date. A single reservation can produce:
  // - a "reservation" card on reservation_date
  // - a "wedding" card on wedding_date
  const getReservationCardsForDate = (date) => {
    const targetStr = date.toDateString();
    const cards = [];

    reservations.forEach((reservation) => {
      if (reservation.reservation_date) {
        const reservationDate = new Date(reservation.reservation_date);
        if (reservationDate.toDateString() === targetStr) {
          cards.push({
            reservation,
            cardType: 'reservation', // reservation date card
          });
        }
      }

      if (reservation.wedding_date) {
        const weddingDate = new Date(reservation.wedding_date);
        if (weddingDate.toDateString() === targetStr) {
          cards.push({
            reservation,
            cardType: 'wedding', // wedding date card
          });
        }
      }
    });

    return cards;
  };


  const handleAddReservationClick = () => {
    setIsAddModalOpen(true);
  };

  const handleReservationAdded = () => {
    fetchReservations(); // Refresh the reservations list
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedReservation(null);
  };

  const handleReservationClick = (reservation) => {
    setSelectedReservation(reservation);
    setIsEditModalOpen(true);
  };

  const handleSearchReservations = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`/api/salons/${salonId}/reservations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          search: searchQuery.trim()
        }
      });
      
      setSearchResults(response.data.reservations || []);
    } catch (error) {
      console.error('Error searching reservations:', error);
      setSearchError('Failed to search reservations');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [salonId]);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search - search after 300ms of no typing
    if (value.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearchReservations(value);
      }, 300);
    } else {
      setSearchResults([]);
    }
  };

  const handleOpenSearchModal = () => {
    setIsSearchModalOpen(true);
    setSearchTerm('');
    setSearchResults([]);
    setSearchError('');
  };

  const handleCloseSearchModal = () => {
    setIsSearchModalOpen(false);
    setSearchTerm('');
    setSearchResults([]);
    setSearchError('');
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  const handleSearchResultClick = (reservation) => {
    setSelectedReservation(reservation);
    setIsSearchModalOpen(false);
    setIsEditModalOpen(true);
    setSearchTerm('');
    setSearchResults([]);
  };

  const renderDayView = () => {
    const dayCards = getReservationCardsForDate(currentDate);

    // Sort cards by underlying wedding date (fallback to reservation date)
    dayCards.sort((a, b) => {
      const aDate = a.reservation.wedding_date || a.reservation.reservation_date;
      const bDate = b.reservation.wedding_date || b.reservation.reservation_date;
      return new Date(aDate) - new Date(bDate);
    });

    return (
      <div className="calendar-day-view">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button onClick={() => navigateDay(-1)} className="calendar-nav-btn">
              ← {t('common.previous')}
            </button>
            <button onClick={goToToday} className="calendar-today-btn">
              {t('appointments.today')}
            </button>
            <button onClick={() => navigateDay(1)} className="calendar-nav-btn">
              {t('common.next')} →
            </button>
          </div>
          <DatePickerButton
            currentDate={currentDate}
            viewMode={viewMode}
            onDateChange={goToDate}
            getMonthName={getMonthName}
            getDayName={getDayName}
            className="calendar-title"
          />
        </div>
        
        <div className="calendar-day-content">
          {dayCards.length === 0 ? (
            <div className="no-appointments">
              <p>{t('reservations.noReservations')}</p>
            </div>
          ) : (
            <div className="day-appointments-list">
              {dayCards.map(({ reservation, cardType }, index) => {
                const isRental = reservation.transaction_type === 'rental';
                const transactionLabel = isRental ? t('reservations.rental') : t('reservations.sale');
                const icon = cardType === 'wedding' ? '💒' : '📅';
                const cardLabel = cardType === 'wedding'
                  ? t('reservations.wedding')
                  : `${t('reservations.reservation')} - ${transactionLabel}`;

                return (
                  <div 
                    key={index} 
                    className={`reservation-item ${reservation.transaction_type} ${cardType}-card`}
                    onClick={() => handleReservationClick(reservation)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="appointment-main-content">
                      <div className="reservation-header-line">
                        <span className="reservation-icon">{icon}</span>
                        <span className="reservation-type-label">
                          {cardLabel}
                        </span>
                        {!!reservation.paid_in_full && userRole !== 'member' && (
                          <FontAwesomeIcon icon={faCircleCheck} className="paid-check-icon" />
                        )}
                      </div>
                      <div className="appointment-client">
                        {reservation.bride_name} {reservation.bride_surname}
                        {reservation.bride_city ? ` - ${reservation.bride_city}` : ''}
                      </div>
                      <div className="reservation-dress">
                        {reservation.dress_name || 'Unknown Dress'}
                      </div>
                      {cardType === 'reservation' && (
                        <div className="appointment-wedding-date">
                          <span>
                            {t('appointments.weddingDate')}: {formatWeddingDate(reservation.wedding_date)}
                          </span>
                        </div>
                      )}
                      {(() => {
                        const reservationNote = buildReservationNotesDisplay(reservation, t);
                        return reservationNote ? (
                          <div className="appointment-notes">
                            {reservationNote}
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div className="appointment-contact-buttons">
                      {reservation.bride_telephone && (
                        <button
                          className="contact-btn phone-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`tel:${reservation.bride_telephone}`, '_self');
                          }}
                          title={`${t('appointments.call')} ${reservation.bride_telephone}`}
                        >
                          📞
                        </button>
                      )}
                      {reservation.bride_telephone && (
                        <button
                          className="contact-btn viber-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const viberUrl = `viber://chat?number=${reservation.bride_telephone}`;
                            window.open(viberUrl, '_self');
                          }}
                          title={t('appointments.openViberChat')}
                        >
                          <FontAwesomeIcon icon={faViber} />
                        </button>
                      )}
                      {reservation.bride_instagram && (
                        <button
                          className="contact-btn instagram-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const instagramUrl = reservation.bride_instagram.startsWith('@') 
                              ? `https://instagram.com/${reservation.bride_instagram.substring(1)}`
                              : `https://instagram.com/${reservation.bride_instagram}`;
                            window.open(instagramUrl, '_blank');
                          }}
                          title={t('appointments.openInstagram')}
                        >
                          <FontAwesomeIcon icon={faInstagram} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates(currentDate);
    
    return (
      <div className="calendar-week-view">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button onClick={() => navigateWeek(-1)} className="calendar-nav-btn">
              ← {t('common.previous')}
            </button>
            <button onClick={goToToday} className="calendar-today-btn">
              {t('appointments.today')}
            </button>
            <button onClick={() => navigateWeek(1)} className="calendar-nav-btn">
              {t('common.next')} →
            </button>
          </div>
          <DatePickerButton
            currentDate={currentDate}
            viewMode={viewMode}
            onDateChange={goToDate}
            getMonthName={getMonthName}
            getDayName={getDayName}
            className="calendar-title"
          />
        </div>
        
        <div className="calendar-grid">
          <div className="calendar-days-header">
            {weekDates.map((date, index) => (
              <div key={index} className="calendar-day-header">
                <div className="day-name">{getDayName(date.getDay(), true)}</div>
                <div className="day-number">{date.getDate()}</div>
              </div>
            ))}
          </div>
          
          <div className="calendar-days-content">
            {weekDates.map((date, index) => {
              const dayCards = getReservationCardsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              
              return (
                <div key={index} className={`calendar-day ${isToday ? 'today' : ''}`}>
                  <div className="day-reservations">
                    {dayCards.map(({ reservation, cardType }, resIndex) => {
                      const icon = cardType === 'wedding' ? '💒' : '📅';
                      const transactionLabel =
                        reservation.transaction_type === 'rental'
                          ? t('reservations.rental')
                          : t('reservations.sale');
                      const weekLabel = cardType === 'wedding'
                        ? t('reservations.wedding')
                        : transactionLabel;
                      return (
                        <div 
                          key={resIndex} 
                          className={`reservation-item ${reservation.transaction_type} ${cardType}-card`}
                          onClick={() => handleReservationClick(reservation)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="reservation-icon-line">
                            <span className="reservation-icon">{icon}</span>
                            <span className="reservation-type">
                              {weekLabel}
                            </span>
                            {!!reservation.paid_in_full && userRole !== 'member' && (
                              <FontAwesomeIcon icon={faCircleCheck} className="paid-check-icon" />
                            )}
                          </div>
                          <div className="reservation-bride">
                            {reservation.bride_name} {reservation.bride_surname}
                          </div>
                          <div className="reservation-dress">
                            {reservation.dress_name}
                          </div>
                          {cardType === 'reservation' && (
                            <div className="reservation-wedding-date">
                              <span className="desktop-only">
                                {t('appointments.weddingDate')}: {formatWeddingDate(reservation.wedding_date)}
                              </span>
                              <span className="mobile-only">
                                {formatWeddingDate(reservation.wedding_date, true)}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const monthDates = getMonthDates(currentDate);

    // Hide the 6th row only if it contains no days from the current month
    let datesToRender = monthDates;
    if (monthDates.length === 42) {
      const lastRow = monthDates.slice(35, 42);
      const hasCurrentMonthDay = lastRow.some(
        (d) => d.getMonth() === currentDate.getMonth()
      );
      if (!hasCurrentMonthDay) {
        datesToRender = monthDates.slice(0, 35);
      }
    }

    return (
      <div className="calendar-month-view">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button onClick={() => navigateMonth(-1)} className="calendar-nav-btn">
              ← {t('common.previous')}
            </button>
            <button onClick={goToToday} className="calendar-today-btn">
              {t('appointments.today')}
            </button>
            <button onClick={() => navigateMonth(1)} className="calendar-nav-btn">
              {t('common.next')} →
            </button>
          </div>
          <DatePickerButton
            currentDate={currentDate}
            viewMode={viewMode}
            onDateChange={goToDate}
            getMonthName={getMonthName}
            getDayName={getDayName}
            className="calendar-title"
          />
        </div>
        
        <div className="calendar-grid month-grid">
          <div className="calendar-days-header">
            {[1, 2, 3, 4, 5, 6, 0].map(dayIndex => (
              <div key={dayIndex} className="calendar-day-header">{getDayName(dayIndex, true)}</div>
            ))}
          </div>
          
          <div className="calendar-days-content month-content">
            {datesToRender.map((date, index) => {
              const dayCards = getReservationCardsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const isCurrentMonth = date.getMonth() === currentDate.getMonth();
              
              return (
                <div key={index} className={`calendar-day month-day ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}>
                  <div className="day-number">{date.getDate()}</div>
                  <div className="day-reservations">
                    {dayCards.slice(0, 3).map(({ reservation, cardType }, resIndex) => {
                      const icon = cardType === 'wedding' ? '💒' : '📅';
                      const brideFullName = `${reservation.bride_name || ''} ${reservation.bride_surname || ''}`.trim();
                      return (
                        <div 
                          key={resIndex} 
                          className={`reservation-item ${reservation.transaction_type} ${cardType}-card`}
                          onClick={() => handleReservationClick(reservation)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="reservation-icon-line">
                            <span className="reservation-icon">{icon}</span>
                            {cardType === 'wedding' && (
                              <span className="reservation-type">{t('reservations.wedding')}</span>
                            )}
                            {!!reservation.paid_in_full && userRole !== 'member' && (
                              <FontAwesomeIcon icon={faCircleCheck} className="paid-check-icon" />
                            )}
                          </div>
                          {brideFullName && (
                            <div className="reservation-bride reservation-bride-month">
                              {brideFullName}
                            </div>
                          )}
                          <div className="reservation-dress">
                            {reservation.dress_name}
                          </div>
                        </div>
                      );
                    })}
                    {dayCards.length > 3 && (
                      <div className="more-reservations">
                        +{dayCards.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="manage-page">
        <div className="loading-message">
          <div className="loading-spinner"></div>
          {t('messages.loading.reservations')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-page">
        <div className="error-message">
          {error}
          <button onClick={fetchReservations} className="salon-button secondary">
            {t('common.search')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-page">
      <div className="table-header">
        <div className="workspace-title">
          <h3>{t('reservations.title')}</h3>
          <div className="view-controls">
            <button 
              className={`view-btn ${viewMode === 'day' ? 'active' : ''}`}
              onClick={handleSwitchToDayView}
            >
              {t('appointments.viewDay')}
            </button>
            <button 
              className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
              onClick={handleSwitchToWeekView}
            >
              {t('appointments.viewWeek')}
            </button>
            <button 
              className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
              onClick={handleSwitchToMonthView}
            >
              {t('appointments.viewMonth')}
            </button>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="salon-button secondary"
            onClick={handleOpenSearchModal}
            title={t('reservations.searchReservations')}
          >
            <FontAwesomeIcon icon={faSearch} style={{ marginRight: '8px' }} />
            {t('common.search')}
          </button>
          <button 
            className="salon-button primary"
            onClick={handleAddReservationClick}
          >
            {t('reservations.addReservation')}
          </button>
        </div>
      </div>
      
      <div className="calendar-container">
        {viewMode === 'day' && renderDayView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
      </div>

      {/* Add Reservation Modal */}
      <AddEditReservationModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        salonId={salonId}
        onReservationAdded={handleReservationAdded}
        mode="add"
        prefillData={window.reservationPrefillData}
        userRole={userRole}
      />

      {/* Edit Reservation Modal */}
      <AddEditReservationModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedReservation(null);
        }}
        salonId={salonId}
        onReservationAdded={handleReservationAdded}
        reservation={selectedReservation}
        mode="edit"
        userRole={userRole}
      />

      {/* Search Reservations Modal */}
      {isSearchModalOpen && (
        <ReservationSearchModal
          isOpen={isSearchModalOpen}
          onClose={handleCloseSearchModal}
          searchTerm={searchTerm}
          onSearchChange={handleSearchInputChange}
          searchResults={searchResults}
          searchLoading={searchLoading}
          searchError={searchError}
          onResultClick={handleSearchResultClick}
          formatWeddingDate={formatWeddingDate}
          t={t}
        />
      )}
    </div>
  );
};

// Reservation Search Modal Component (not exported)
const ReservationSearchModal = ({ 
  isOpen, 
  onClose, 
  searchTerm, 
  onSearchChange, 
  searchResults, 
  searchLoading, 
  searchError,
  onResultClick,
  formatWeddingDate,
  t
}) => {
  useScrollLock(isOpen);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h3>{t('reservations.searchReservations')}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="search-container" style={{ marginBottom: '20px' }}>
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder={t('reservations.searchByBrideName')}
              value={searchTerm}
              onChange={onSearchChange}
            />
          </div>

          {searchError && (
            <div className="error-message" style={{ marginBottom: '20px' }}>
              {searchError}
            </div>
          )}

          {searchLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="loading-spinner"></div>
              <p>{t('messages.loading.searching')}</p>
            </div>
          )}

          {!searchLoading && searchTerm.trim().length >= 2 && searchResults.length === 0 && !searchError && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
              <p>{t('reservations.noResultsFound')}</p>
            </div>
          )}

          {!searchLoading && searchTerm.trim().length < 2 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
              <p>{t('reservations.enterAtLeastTwoCharacters')}</p>
            </div>
          )}

          {!searchLoading && searchResults.length > 0 && (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {searchResults.map((reservation) => (
                  <div
                    key={reservation.id}
                    onClick={() => onResultClick(reservation)}
                    style={{
                      padding: '16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#fff'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f7fafc';
                      e.currentTarget.style.borderColor = '#667eea';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '1.1rem', color: '#2d3748', marginBottom: '4px' }}>
                          {reservation.bride_name} {reservation.bride_surname}
                        </div>
                        {reservation.dress_name && (
                          <div style={{ color: '#718096', fontSize: '0.95rem', marginBottom: '4px' }}>
                            {t('reservations.dress')}: {reservation.dress_name}
                          </div>
                        )}
                        {reservation.wedding_date && (
                          <div style={{ color: '#718096', fontSize: '0.95rem' }}>
                            {t('reservations.weddingDate')}: {formatWeddingDate(reservation.wedding_date)}
                          </div>
                        )}
                      </div>
                      <div style={{ 
                        padding: '4px 12px', 
                        borderRadius: '8px', 
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        backgroundColor: reservation.transaction_type === 'rental' ? '#e6fffa' : '#fff5f5',
                        color: reservation.transaction_type === 'rental' ? '#047857' : '#c53030'
                      }}>
                        {reservation.transaction_type === 'rental' ? t('reservations.rental') : t('reservations.sale')}
                      </div>
                    </div>
                    {reservation.bride_telephone && (
                      <div style={{ color: '#4a5568', fontSize: '0.9rem', marginTop: '8px' }}>
                        📞 {reservation.bride_telephone}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageReservations;
